import { useNavigate } from 'react-router';
import { Box, Center, Column, CustomSpinner, Icon, LineClamp, Row, Text } from '~/commonComponents';
import { IPendingReview } from '~/domain/ShiftReview';
import { UserProfession } from '~/enums/UserProfession';
import { Colors } from '~/themes/colors';
import { TableHeader } from './TableHeader';
import { useMediaQuery } from '~/hooks';
import { ExclamationCircleIcon } from '~/images/icons/ExclamationCircleIcon';
import { ClockEndIcon } from '~/images/icons/ClockEndIcon';
import { AdjustmentDetailV1 } from './AdjustmentDetailV1';
import { AdjustmentDetailV2 } from './AdjustmentDetailV2';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnforceOfficeDashboardV2 } from '~/constants/ff.posthog';
import { fireDashboardPendingReviewClicked } from '~/posthogs/events';

interface IProps {
  data: IPendingReview[];
  isLoading?: boolean;
}

export const Table = ({ data = [], isLoading = false }: IProps) => {
  const navigate = useNavigate();
  const [isSmallScreen] = useMediaQuery(['lg']);

  const useOfficeDashboardV2 = useFeatureFlag(EnforceOfficeDashboardV2);

  const handleRowClick = (item: IPendingReview) => {
    fireDashboardPendingReviewClicked();
    if (item.hasAdjustment) {
      navigate(`/dashboard/adjustments/${item?.id}`, {
        state: { job_id: item.id },
      });
      return;
    }

    navigate(`/dashboard/job/${item.id}/details`);
  };

  return (
    <table
      className="active-shifts-table"
      data-cy="pending-reviews-table"
      style={{ borderCollapse: 'collapse', width: '100%' }}
    >
      <TableHeader />
      <tbody>
        {isLoading && (
          <tr>
            <td colSpan={6}>
              <Center>
                <CustomSpinner isLoading />
              </Center>
            </td>
          </tr>
        )}

        {!isLoading &&
          data.map((it) => (
            <tr
              key={it.id}
              data-cy={`pending-review-row-${it.id}`}
              style={{ borderBottom: `2px solid ${Colors.neutral_divider}` }}
              onClick={() => handleRowClick(it)}
            >
              <td style={{ paddingTop: '14px', paddingBottom: '8px' }}>
                <img
                  src={it.professional.image}
                  style={{
                    border: `1px solid ${Colors.neutral_100}`,
                    borderRadius: 60,
                    height: 60,
                    objectFit: 'contain',
                    width: 60,
                  }}
                  alt="professional image"
                />
              </td>
              <td style={{ paddingTop: '14px', paddingBottom: '8px', paddingRight: '10px' }}>
                <Column>
                  <Text bold fontSize={18}>
                    {it.professional.fullName}
                  </Text>
                  <Text color={Colors.neutral_500}>{UserProfession.getName(it.profession)}</Text>
                </Column>
              </td>
              {!isSmallScreen && (
                <td style={{ paddingTop: '14px', paddingBottom: '8px', width: '30%' }}>
                  <LineClamp
                    lineClamp={2}
                    text={it.offerOwner.officeName}
                    style={{ color: Colors.neutral_500 }}
                  />
                </td>
              )}
              <td style={{ paddingTop: '14px', paddingBottom: '8px', paddingRight: '10px' }}>
                {useOfficeDashboardV2 ? (
                  <AdjustmentDetailV2 it={it} />
                ) : (
                  <AdjustmentDetailV1 it={it} />
                )}
              </td>
              <td
                data-cy={!it.hasAdjustment ? 'confirm-shift-action' : 'review-shift-action'}
                style={{ paddingTop: '14px', paddingBottom: '8px' }}
              >
                {!it.hasAdjustment ? (
                  <Row alignItems="center" gap={5}>
                    <ClockEndIcon />
                    <Text bold fontSize={18}>
                      Confirm
                    </Text>
                    <Box marginLeft={11}>
                      <Icon name="arrow-right" size={24} />
                    </Box>
                  </Row>
                ) : (
                  <Row alignItems="center" gap={8}>
                    <ExclamationCircleIcon />
                    <Text bold fontSize={18}>
                      Review
                    </Text>
                    <Box marginLeft={11}>
                      <Icon name="arrow-right" size={24} />
                    </Box>
                  </Row>
                )}
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  );
};
